import { Flex, Popover, Portal, useDisclosure } from "@chakra-ui/react"
import { BlockLayoutType } from "@pathwright/blocks-core"
import { BlockType, useEditorStateSelector } from "@pathwright/blocks-engine"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import React from "react"
import styled from "styled-components"
import BlockLayoutPreview from "./BlockLayoutPreview"
import { getBlockTypeLabel, getBlockTypeLayoutOptions } from "./utils"

const LayoutSwitcherContainer = styled.div`
  display: flex;
  padding: 10px;
  border-radius: 8px;
  background-color: #ffffff;
  box-shadow: 0px 0px 30px rgba(0, 0, 0, 0.18);
`

const LayoutSwitcherPreviewContainer = styled.div`
  padding: 10px;
`

const LayoutMenu = styled(Flex)`
  width: 100%;
`

const LayoutItem = styled.div<{ $selected: boolean }>`
  padding: 5px 10px;
  width: 100%;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => (p.$selected ? "#2A2E33" : "#666")};
  border-radius: 12px;
  &:hover {
    background-color: #efefef;
  }
`

const DropdownButton = styled.div`
  cursor: pointer;

  @media only screen and (max-width: 400px) {
    font-size: 0.9em;
  }

  @media only screen and (max-width: 350px) {
    font-size: 0.7em;
  }
`

const LayoutLabel = styled.span`
  display: flex;
  align-items: center;
  white-space: pre;
`

const propsAreEqual = (
  prevProps: BlockLayoutSwitcherProps,
  nextProps: BlockLayoutSwitcherProps
) => {
  if (prevProps.block !== nextProps.block) return false

  return true
}

type BlockLayoutSwitcherProps = {
  onSelectLayout: (layout: BlockLayoutType["key"]) => void
  block: BlockType
}

const BlockLayoutSwitcher = React.memo((props: BlockLayoutSwitcherProps) => {
  const { onSelectLayout, block } = props
  const disclosure = useDisclosure()

  const { layout, type, data } = block

  const blockTypes = useEditorStateSelector((state) => state.blockTypes)

  const [selectLayoutIndex, setSelectedLayoutIndex] = React.useState<
    number | null
  >(null)
  const options = React.useMemo(() => {
    if (type && blockTypes) {
      return getBlockTypeLayoutOptions({ type, blockTypes })
    }
    return []
  }, [type, blockTypes, layout])

  React.useEffect(() => {
    const selectLayoutIndex = options.findIndex((op) => op.key === layout)
    if (selectLayoutIndex === -1) return
    setSelectedLayoutIndex(selectLayoutIndex)
  }, [layout, options])

  const resetPreviewIndex = () => {
    const index = options.findIndex((op) => op.key === layout)
    if (index === -1) return
    setSelectedLayoutIndex(index >= 0 ? index : null)
  }

  const handleOpenChange = (e: { open: boolean }) => {
    if (e.open) {
      disclosure.onOpen()
    } else {
      resetPreviewIndex()
      disclosure.onClose()
    }
  }

  const handleSelectLayout = (option: BlockLayoutType) => () => {
    onSelectLayout(option.key)
    disclosure.onClose()
  }

  const renderLayoutPreview = () => {
    if (!options.length) return null

    const selectedLayout = options[selectLayoutIndex ?? 0] || {
      key: "",
      data: {}
    }

    const passProps = {
      ...block,
      layout: selectedLayout.key,
      data: { ...selectedLayout.data, ...data }
    }

    return <BlockLayoutPreview {...passProps} />
  }

  return (
    <Popover.Root
      open={disclosure.open}
      positioning={{
        placement: "bottom-start",
        offset: { crossAxis: -5 }
      }}
      closeOnInteractOutside
      closeOnEscape
      lazyMount
      onOpenChange={handleOpenChange}
    >
      <Tooltip
        title={!disclosure.open ? "Choose a layout for this block" : undefined}
      >
        <Popover.Trigger asChild>
          <DropdownButton>
            <LayoutLabel>
              {getBlockTypeLabel({ type, blockTypes, layout })}
              <span> </span>
              <Pathicon icon="chevron-down" />
            </LayoutLabel>
          </DropdownButton>
        </Popover.Trigger>
      </Tooltip>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            p={0}
            bg="transparent"
            boxShadow="none"
            border="none"
          >
            <Popover.Body p={0}>
              <LayoutSwitcherContainer>
                <LayoutMenu direction="column">
                  {options.map((option, i) => {
                    const selected = selectLayoutIndex === i
                    return (
                      <LayoutItem
                        key={i}
                        $selected={selected}
                        onMouseEnter={() => setSelectedLayoutIndex(i)}
                        onClick={handleSelectLayout(option)}
                      >
                        {option.label}
                      </LayoutItem>
                    )
                  })}
                </LayoutMenu>
                <LayoutSwitcherPreviewContainer
                  onClick={() => {
                    const option = options[selectLayoutIndex ?? 0]
                    if (option) handleSelectLayout(option)()
                  }}
                >
                  {renderLayoutPreview()}
                </LayoutSwitcherPreviewContainer>
              </LayoutSwitcherContainer>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}, propsAreEqual)
BlockLayoutSwitcher.displayName = "BlockLayoutSwitcher"

export default BlockLayoutSwitcher
